// ── White-Label Brand Configuration ──────────────────────────────────────────
// Set VITE_BRAND in .env to select a brand. Defaults to 'mef'.
// Each deployment (Firebase Hosting target) uses its own .env file.

export interface WhiteLabelConfig {
  appName: string;
  appShortName: string;
  appTagline: string;
  logoUrl: string | null;           // null = use text logo
  faviconEmoji: string;
  primaryColor: string;             // hex — main brand colour
  primaryDark: string;              // hex — darker shade for hover/borders
  accentColor: string;              // hex — secondary highlight colour
  supportEmail: string;
  supportPhone: string;
  defaultSchoolName: string;
  defaultSchoolAddress: string;
  websiteUrl: string;
  copyrightHolder: string;
  poweredByLabel: string;           // shown in footer: "Powered by MEF Edusuite"
  modules: {
    tahfiz: boolean;
    musabaqa: boolean;
    liveClassroom: boolean;
    aiTutor: boolean;
    cbt: boolean;
    financialManagement: boolean;
    iqaHandbook: boolean;
    lessonVideoGenerator: boolean;
    mefPresentation: boolean;
    curriculum: boolean;
    superAdmin: boolean;
  };
}

// ── MEF (default) ─────────────────────────────────────────────────────────────
const mef: WhiteLabelConfig = {
  appName: 'MEF Edusuite',
  appShortName: 'MEF',
  appTagline: 'Powered by Muyikaratu Educational Foundation',
  logoUrl: null,
  faviconEmoji: '🎓',
  primaryColor: '#1B3870',
  primaryDark: '#122657',
  accentColor: '#3D9B3D',
  supportEmail: 'info@muyikaratu.com',
  supportPhone: '+234 (0) 909 222 0178',
  defaultSchoolName: 'MEF EduSuite',
  defaultSchoolAddress: 'No. 1 Uganda Street, Malali Low-Cost, Kaduna, Nigeria',
  websiteUrl: 'https://kca-portal.web.app',
  copyrightHolder: 'Muyikaratu Educational Foundation',
  poweredByLabel: '',
  modules: {
    tahfiz: true,
    musabaqa: true,
    liveClassroom: true,
    aiTutor: true,
    cbt: true,
    financialManagement: true,
    iqaHandbook: true,
    lessonVideoGenerator: true,
    mefPresentation: true,
    curriculum: true,
    superAdmin: true,
  },
};

// ── Al-Qari ───────────────────────────────────────────────────────────────────
const alqari: WhiteLabelConfig = {
  appName: 'Al-Qari',
  appShortName: 'Al-Qari',
  appTagline: "Empowering Hafizs · Preserving Sacred Knowledge",
  logoUrl: null,
  faviconEmoji: '📖',
  primaryColor: '#1B5E20',          // deep Islamic green
  primaryDark: '#145218',
  accentColor: '#D4A017',           // gold accent
  supportEmail: 'support@alqari.ng',
  supportPhone: '+234 (0) 800 000 0000',
  defaultSchoolName: 'Al-Qari',
  defaultSchoolAddress: 'Abuja, Nigeria',
  websiteUrl: 'https://app.alqari.ng',
  copyrightHolder: 'Al-Qari Foundation',
  poweredByLabel: 'Powered by MEF Edusuite',
  modules: {
    tahfiz: true,
    musabaqa: true,
    liveClassroom: true,
    aiTutor: true,
    cbt: true,
    financialManagement: true,
    iqaHandbook: false,
    lessonVideoGenerator: true,
    mefPresentation: false,
    curriculum: false,
    superAdmin: false,
  },
};

const BRANDS: Record<string, WhiteLabelConfig> = { mef, alqari };

// Active brand — set VITE_BRAND=alqari in .env.alqari
const brandKey = (import.meta as any).env?.VITE_BRAND || 'mef';
export const brand: WhiteLabelConfig = BRANDS[brandKey] ?? mef;

/** Injects brand CSS variables and sets document title. Call once in main.tsx. */
export function applyBrand(): void {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', brand.primaryColor);
  root.style.setProperty('--brand-primary-dark', brand.primaryDark);
  root.style.setProperty('--brand-accent', brand.accentColor);
  document.title = brand.appName;
  // Update favicon emoji
  const favicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (favicon) {
    favicon.href = `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>${brand.faviconEmoji}</text></svg>`;
  }
}
